import axiosClient from './axiosClient';
import { getFirstTouch } from '../lib/attribution';
import { getAnonId } from '../lib/conversionTracking';
import { clearReferral, getReferralCode } from '../lib/referral';
import { UserProfile } from '../types'; // Assuming UserProfile type is defined in types.ts

/**
 * The campaign that first brought this browser to the site, sent once when an
 * account is created so it can be stored against the account itself.
 *
 * `attribution.ts` has always captured this, but it only ever travelled on
 * analytics events — which means it existed for as long as GA4 kept it, only for
 * visitors who accepted measurement, and could never be joined to a payment or a
 * certificate. Sending it here is what makes cost per registration by channel a
 * question the database can answer.
 *
 * Click identifiers are deliberately excluded: they identify a click, not a
 * channel, and are meaningless once stored.
 *
 * The advertisement's own identifiers ARE sent, and are not click identifiers.
 * Spend is exported keyed on them while conversions arrive keyed on whatever
 * label somebody typed into an ad builder, so without them "which ad made this
 * person open an account" can only be answered at whatever granularity the
 * naming convention happened to survive — campaign level on a good day, and
 * nothing at all when two ads share a name. `sanitizeAttribution` on the server
 * still stores only the seven channel labels on the account itself; the ids
 * travel to the funnel row, which is where the spend join happens.
 */
const firstTouchForSignup = () => {
  const {
    utm_source, utm_medium, utm_campaign, utm_content, utm_term, referrer, landing_page,
    campaign_id, adset_id, ad_id, creative_id,
  } = getFirstTouch();
  return {
    utm_source, utm_medium, utm_campaign, utm_content, utm_term, referrer, landing_page,
    campaign_id, adset_id, ad_id, creative_id,
  };
};

export type AuthResult = { token: string; user: UserProfile; isNewUser?: boolean };

export const login = async (email: string, password: string): Promise<AuthResult> => {
  const response = await axiosClient.post('/auth/login', { email, password });
  return response.data;
};

/**
 * `agreeToTerms` is the sign-up form's Terms/Privacy checkbox, which was
 * validated in the browser and then thrown away — it never reached the server,
 * so no account carried any record of the consent it was created under. The
 * server now refuses a registration without it and stamps `termsAcceptedAt` /
 * `termsVersion` on the account when it is present.
 *
 * It defaults to `true` because the only path that reaches this function is the
 * sign-up form, whose Yup schema and explicit guard both block submission until
 * the box is ticked — so by the time we are here, it has been ticked. Passing
 * the real value explicitly is better still, and callers that hold it should;
 * the default exists so the auth context's three-argument `register` keeps
 * working rather than silently failing every sign-up.
 */
export const register = async (
  email: string,
  password: string,
  displayName: string,
  /**
   * Required, deliberately not defaulted: the server stamps this against a
   * terms version, so a caller must state what the person actually did.
   */
  agreeToTerms: boolean,
): Promise<AuthResult> => {
  const response = await axiosClient.post('/auth/register', {
    email,
    password,
    displayName,
    agreeToTerms,
    attribution: firstTouchForSignup(),
    /**
     * The browser, so the server can join the anonymous ad clicks this person
     * already made to the account they have just opened.
     *
     * Undefined without analytics consent, by design — `getAnonId` mints and
     * reads nothing without it. The registration is still recorded and still
     * attributed to its campaign; it simply is not joined to a click.
     */
    anonId: getAnonId(),
    /**
     * The partner who introduced them, if a link did.
     *
     * A hint, not an instruction — the server checks the code is live, refuses a
     * self-referral, and refuses to overwrite a referral this account already
     * has. Cleared once the account exists, because from then on the server's
     * own record is the one that counts and a leftover code could only
     * mis-credit a second signup on the same browser.
     */
    referralCode: getReferralCode(),
  });
  clearReferral();
  return response.data;
};

/**
 * One endpoint serves both "sign up with Google" and "sign in with Google" —
 * the caller cannot know which it will be, so attribution is always sent and the
 * server stores it only when it actually creates an account.
 */
export const signInWithGoogle = async (idToken: string): Promise<AuthResult> => {
  const response = await axiosClient.post('/auth/google-signin', {
    idToken,
    attribution: firstTouchForSignup(),
    // Sent unconditionally for the same reason attribution is: the caller cannot
    // know whether this call will create an account, and the server uses it only
    // on the branch that does.
    anonId: getAnonId(),
    referralCode: getReferralCode(),
  });
  // Only a genuinely new account consumed the code; the server ignores it for a
  // sign-in. Clearing on both outcomes is still right — somebody who already has
  // an account is not going to be referred by this link either.
  if (response.data?.isNewUser) clearReferral();
  return response.data;
};

export const resetPasswordRequest = async (email: string): Promise<void> => {
  await axiosClient.post('/auth/forgot-password', { email });
};

export const resetPasswordConfirm = async (token: string, newPassword: string): Promise<void> => {
  await axiosClient.post(`/auth/reset-password/${token}`, { password: newPassword });
};

export const verifyEmail = async (token: string): Promise<void> => {
  await axiosClient.post(`/auth/verify-email?token=${encodeURIComponent(token)}`);
};

export const resendVerification = async (email: string): Promise<void> => {
  await axiosClient.post('/auth/resend-verification', { email });
};

export const logout = async (): Promise<void> => {
  // Backend might have a logout endpoint to invalidate tokens or clear sessions
  // For JWTs, client-side removal is often sufficient, but a backend call can be added for completeness
  await axiosClient.post('/auth/logout');
};
